export const SAAS_HOSTED_PROVIDERS = [
  { provider: "groq", label: "Groq" },
  { provider: "openai", label: "OpenAI" },
  { provider: "anthropic", label: "Anthropic" },
  { provider: "gemini", label: "Gemini" },
];

export const SAAS_PROVIDER_LABELS = SAAS_HOSTED_PROVIDERS.reduce((labels, option) => {
  labels[option.provider] = option.label;
  return labels;
}, {});

const numberOrNull = (value) => {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const integerOrNull = (value) => {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) return 0;
  if (!/^-?\d+$/.test(cleaned)) return null;
  return Number.parseInt(cleaned, 10);
};

export function getSaasProviderLabel(provider) {
  return SAAS_PROVIDER_LABELS[provider] || provider || "Not configured";
}

export function normalizeSaasProviderOptions(optionsResponse) {
  const hostedProviders =
    optionsResponse?.hosted_providers ||
    optionsResponse?.providers ||
    optionsResponse?.options?.hosted_providers ||
    [];

  if (!Array.isArray(hostedProviders) || hostedProviders.length === 0) {
    return SAAS_HOSTED_PROVIDERS;
  }

  return hostedProviders
    .filter((provider) => provider?.provider || provider?.value || provider?.id)
    .map((provider) => {
      const value = provider.provider || provider.value || provider.id;
      return {
        ...provider,
        provider: value,
        label: provider.label || provider.display_name || getSaasProviderLabel(value),
      };
    });
}

export function getDefaultSaasProvider(options = SAAS_HOSTED_PROVIDERS) {
  return options[0]?.provider || "groq";
}

export function createEmptySaasRuntimeDraft(provider = "groq") {
  return {
    provider,
    requestTimeoutSeconds: "60",
    maxRetries: "2",
  };
}

export function createEmptySaasCredential(provider = "groq") {
  return {
    provider,
    displayName: "",
    apiKey: "",
  };
}

export function getSaasCredentialPreview({ companyName, provider, displayName }) {
  const explicitName = String(displayName || "").trim();
  if (explicitName) {
    return explicitName;
  }
  const label = getSaasProviderLabel(provider);
  const tenantName = String(companyName || "").trim();
  return tenantName ? `${tenantName} ${label} Key` : `Tenant ${label} Key`;
}

export function validateSaasRuntimeDraft(draft) {
  const errors = {};
  const timeout = numberOrNull(draft.requestTimeoutSeconds);
  const retries = integerOrNull(draft.maxRetries);

  if (!String(draft.provider || "").trim()) {
    errors.provider = "Select an LLM provider.";
  }
  if (timeout !== null && timeout <= 0) {
    errors.requestTimeoutSeconds = "Timeout must be greater than 0.";
  }
  if (retries === null) {
    errors.maxRetries = "Retries must be a whole number.";
  } else if (retries < 0) {
    errors.maxRetries = "Retries must be 0 or greater.";
  }

  return errors;
}

export function validateSaasCredential(credential) {
  const errors = {};
  if (!String(credential?.apiKey || "").trim()) {
    errors.apiKey = "API key is required.";
  }
  return errors;
}

function buildCredentialObject(credential, { requireApiKey = false } = {}) {
  const apiKey = String(credential?.apiKey || "").trim();
  const displayName = String(credential?.displayName || "").trim();

  if (!apiKey && !requireApiKey) {
    return null;
  }

  const payload = {};
  if (apiKey) {
    payload.api_key = apiKey;
  }
  if (displayName) {
    payload.display_name = displayName;
  }
  return payload;
}

export function buildSaasDraftPayload(draft, credential = null, options = {}) {
  const payload = {
    provider: String(draft.provider || "").trim(),
    request_timeout_seconds: numberOrNull(draft.requestTimeoutSeconds),
    max_retries: integerOrNull(draft.maxRetries) ?? 0,
  };

  const credentialPayload = buildCredentialObject(credential, options);
  if (credentialPayload) {
    payload.credential = credentialPayload;
  }

  return payload;
}

export function buildSaasCredentialPayload(credential) {
  const displayName = String(credential?.displayName || "").trim();
  const payload = {
    provider: String(credential?.provider || "").trim(),
    api_key: String(credential?.apiKey || "").trim(),
  };
  if (displayName) {
    payload.display_name = displayName;
  }
  return payload;
}

export function buildSaasOnboardingLlmPayload(formState) {
  const provider = String(formState.llmProvider || "").trim();
  const displayName = String(formState.llmCredentialName || "").trim();
  const payload = buildSaasDraftPayload(
    {
      provider,
      requestTimeoutSeconds: formState.llmRequestTimeoutSeconds,
      maxRetries: formState.llmMaxRetries,
    },
    {
      displayName,
      apiKey: formState.llmApiKey,
    },
    { requireApiKey: true }
  );

  return payload;
}

export function profileToSaasDraft(profile) {
  return {
    provider: profile?.provider || "groq",
    requestTimeoutSeconds: String(profile?.request_timeout_seconds ?? 60),
    maxRetries: String(profile?.max_retries ?? 2),
  };
}

export function profileToSaasCredential(profile) {
  return {
    provider: profile?.provider || "groq",
    displayName: profile?.credential?.display_name || "",
    apiKey: "",
  };
}

export function getSaasProfileStatus(profile, activeProfileId) {
  const rawStatus = String(profile?.status || "").toLowerCase();
  if (rawStatus === "active" || profile?.is_active || profile?.id === activeProfileId) {
    return "active";
  }
  if (rawStatus === "draft") {
    return "draft";
  }
  return rawStatus || "inactive";
}
